import { useEffect } from 'react';

const BASE_TITLE = 'Sport Fund | Fundraising for Athletes & Sports Aspirants';
const SHORT_BRAND = 'Sport Fund';
const SITE_ORIGIN = 'https://thesportfund.com';
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-image.png`;

/** Extra, per-page SEO/GEO options. All optional. */
export interface PageMetaOptions {
  /**
   * Canonical path for this page (root-relative, e.g. "/faqs"). Defaults to the
   * current pathname. During prerender each route is visited directly, so the
   * default resolves correctly.
   */
  path?: string;
  /** og:image / twitter:image — absolute or root-relative URL. Defaults to /og-image.png */
  image?: string;
  /** Mark this page as an article (sets og:type=article and emits Article schema). */
  article?: boolean;
  /** One or more JSON-LD objects to inject into <head> (e.g. FAQPage, HowTo). */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /**
   * Human-readable breadcrumb trail (excluding Home, which is prepended).
   * e.g. [{ name: 'Resources', path: '/resources' }, { name: 'Snap! Raise Alternatives' }].
   * When omitted, no BreadcrumbList is emitted.
   */
  breadcrumbs?: { name: string; path?: string }[];
}

const MANAGED_ATTR = 'data-page-meta';

function toAbsolute(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  return `${SITE_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`;
}

function setMeta(selector: string, attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/**
 * Sets document title, meta description, canonical, Open Graph / Twitter tags and
 * optional JSON-LD structured data for the current page.
 *
 * Fixes the SPA canonicalisation problem: without this, every prerendered route
 * inherits the homepage's canonical + OG tags from index.html, so search engines
 * treat them as duplicates of the homepage.
 *
 * @param pageTitle  For the homepage pass undefined/empty; otherwise e.g. "About".
 * @param description Meta description (also used for og/twitter description).
 * @param options    Extra per-page SEO/GEO options (canonical path, image, JSON-LD…).
 */
export function usePageMeta(
  pageTitle: string | undefined,
  description?: string,
  options: PageMetaOptions = {}
) {
  const { path, image, article, jsonLd, breadcrumbs } = options;
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : '';
  const breadcrumbsKey = breadcrumbs ? JSON.stringify(breadcrumbs) : '';

  useEffect(() => {
    // Google truncates (and often outright rewrites) a <title> past ~60 chars.
    // The page's own TITLE already carries the distinctive, click-worthy part
    // (and on comparison pages already says "Sport Fund") -- appending the
    // full homepage tagline pushed every resource-page title past 150 chars,
    // guaranteeing the differentiator got cut from the SERP snippet.
    const fullTitle = pageTitle
      ? pageTitle.toLowerCase().includes('sport fund')
        ? pageTitle
        : `${pageTitle} | ${SHORT_BRAND}`
      : BASE_TITLE;
    document.title = fullTitle;

    const canonicalPath = path ?? window.location.pathname;
    const canonicalUrl = `${SITE_ORIGIN}${canonicalPath === '/' ? '/' : canonicalPath.replace(/\/$/, '')}`;
    const ogImage = toAbsolute(image ?? DEFAULT_OG_IMAGE);

    // Description (also feeds OG/Twitter)
    if (description !== undefined) {
      setMeta('meta[name="description"]', 'name', 'description', description);
      setMeta('meta[property="og:description"]', 'property', 'og:description', description);
      setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    }

    // Canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    // Open Graph + Twitter
    setMeta('meta[property="og:title"]', 'property', 'og:title', fullTitle);
    setMeta('meta[property="og:url"]', 'property', 'og:url', canonicalUrl);
    setMeta('meta[property="og:type"]', 'property', 'og:type', article ? 'article' : 'website');
    setMeta('meta[property="og:image"]', 'property', 'og:image', ogImage);
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', fullTitle);
    setMeta('meta[name="twitter:image"]', 'name', 'twitter:image', ogImage);

    // ---- JSON-LD (managed: cleaned up on route change) ----
    const scripts: HTMLScriptElement[] = [];
    const addJsonLd = (obj: Record<string, unknown>) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute(MANAGED_ATTR, 'true');
      s.textContent = JSON.stringify(obj);
      document.head.appendChild(s);
      scripts.push(s);
    };

    if (breadcrumbs && breadcrumbs.length > 0) {
      const items = [{ name: 'Home', path: '/' }, ...breadcrumbs];
      addJsonLd({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((it, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: it.name,
          ...(it.path ? { item: `${SITE_ORIGIN}${it.path === '/' ? '/' : it.path.replace(/\/$/, '')}` } : {}),
        })),
      });
    }

    if (article) {
      addJsonLd({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: pageTitle ?? BASE_TITLE,
        ...(description !== undefined ? { description } : {}),
        image: ogImage,
        inLanguage: 'en',
        mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
        author: { '@type': 'Organization', name: 'Sport Fund', url: SITE_ORIGIN },
        publisher: {
          '@type': 'Organization',
          name: 'Sport Fund',
          url: SITE_ORIGIN,
          logo: { '@type': 'ImageObject', url: `${SITE_ORIGIN}/sportfund-logo-icon.png` },
        },
      });
    }

    if (jsonLd) {
      (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).forEach(addJsonLd);
    }

    return () => {
      scripts.forEach((s) => s.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTitle, description, path, image, article, jsonLdKey, breadcrumbsKey]);
}
